import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  AttachFile,
  ContentCopy,
  DeleteOutline,
  ExpandMore,
  MailOutline,
  Refresh,
} from '@mui/icons-material';
import { NotaryEmailDraftAttachment, NotaryEmailDraftDTO } from '@inversor-hub/types';
import { useSnackbar } from 'notistack';
import { useGenerateNotaryEmailDraft } from '@/lib/api/investment-checklist';
import { useGetMe } from '@/lib/api/me';
import {
  createNotaryEmailDraftFormValues,
  emptyNotaryParty,
  emptyNotaryRepresentation,
  NotaryEmailDraftFormValues,
  NotaryPartyFormValue,
  NotaryPartyGroupFormValue,
  NotaryRepresentationFormValue,
  toNotaryEmailDraftRequest,
} from './notary-email-draft-form';

interface NotaryEmailDraftDialogProps {
  open: boolean;
  investmentId: string;
  onClose: () => void;
}

interface PartyGroupFieldsProps {
  title: string;
  value: NotaryPartyGroupFormValue;
  emptyMessage: string;
  onChange: (value: NotaryPartyGroupFormValue) => void;
}

const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { error?: string; message?: string } | undefined;
    return data?.message ?? data?.error ?? 'No se pudo generar el borrador.';
  }
  return 'No se pudo generar el borrador.';
};

const PartyGroupFields = ({ title, value, emptyMessage, onChange }: PartyGroupFieldsProps) => {
  const updateParty = (index: number, patch: Partial<NotaryPartyFormValue>) => {
    onChange({
      ...value,
      parties: value.parties.map((party, partyIndex) =>
        partyIndex === index ? { ...party, ...patch } : party
      ),
    });
  };

  const updateRepresentation = (index: number, patch: Partial<NotaryRepresentationFormValue>) => {
    onChange({
      ...value,
      representations: value.representations.map((representation, representationIndex) =>
        representationIndex === index ? { ...representation, ...patch } : representation
      ),
    });
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        {value.parties.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        )}
      </Box>

      {value.parties.map((party, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle2">Persona {index + 1}</Typography>
            <Tooltip title="Eliminar persona">
              <IconButton
                size="small"
                aria-label={`Eliminar persona ${index + 1}`}
                onClick={() =>
                  onChange({
                    ...value,
                    parties: value.parties.filter((_, partyIndex) => partyIndex !== index),
                  })
                }
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre completo"
                value={party.fullName}
                onChange={(event) => updateParty(index, { fullName: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Tipo documento"
                placeholder="DNI / NIE"
                value={party.documentType}
                onChange={(event) => updateParty(index, { documentType: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Número"
                value={party.documentNumber}
                onChange={(event) => updateParty(index, { documentNumber: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Domicilio"
                value={party.address}
                onChange={(event) => updateParty(index, { address: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Estado civil"
                placeholder="Soltera, casado…"
                value={party.civilStatus}
                onChange={(event) => updateParty(index, { civilStatus: event.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Profesión"
                value={party.profession}
                onChange={(event) => updateParty(index, { profession: event.target.value })}
              />
            </Grid>
          </Grid>
        </Paper>
      ))}

      <Button
        variant="outlined"
        size="small"
        startIcon={<Add />}
        onClick={() => onChange({ ...value, parties: [...value.parties, emptyNotaryParty()] })}
        sx={{ alignSelf: 'flex-start' }}
      >
        Añadir persona
      </Button>

      <TextField
        fullWidth
        label="Relación entre las partes"
        placeholder="Ej.: Casados en régimen de gananciales"
        value={value.relationshipStatement}
        onChange={(event) => onChange({ ...value, relationshipStatement: event.target.value })}
      />

      {value.representations.map((representation, index) => (
        <Paper key={index} variant="outlined" sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle2">Representación {index + 1}</Typography>
            <IconButton
              size="small"
              aria-label={`Eliminar representación ${index + 1}`}
              onClick={() =>
                onChange({
                  ...value,
                  representations: value.representations.filter(
                    (_, representationIndex) => representationIndex !== index
                  ),
                })
              }
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                fullWidth
                label="Tipo"
                value={representation.authority}
                onChange={(event) =>
                  updateRepresentation(index, {
                    authority: event.target.value as NotaryRepresentationFormValue['authority'],
                  })
                }
              >
                <MenuItem value="">Sin indicar</MenuItem>
                <MenuItem value="POWER_OF_ATTORNEY">Poder notarial</MenuItem>
                <MenuItem value="OTHER">Otra representación</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Representante"
                value={representation.representativeName}
                onChange={(event) =>
                  updateRepresentation(index, { representativeName: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Persona representada"
                value={representation.representedName}
                onChange={(event) =>
                  updateRepresentation(index, { representedName: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Detalles"
                value={representation.details}
                onChange={(event) => updateRepresentation(index, { details: event.target.value })}
              />
            </Grid>
          </Grid>
        </Paper>
      ))}

      <Button
        variant="text"
        size="small"
        startIcon={<Add />}
        onClick={() =>
          onChange({
            ...value,
            representations: [...value.representations, emptyNotaryRepresentation()],
          })
        }
        sx={{ alignSelf: 'flex-start' }}
      >
        Añadir representación
      </Button>
    </Stack>
  );
};

const AttachmentList = ({
  attachments,
  selectedIds,
  onToggle,
}: {
  attachments: NotaryEmailDraftAttachment[];
  selectedIds: Set<string>;
  onToggle: (mediaId: string) => void;
}) => {
  if (!attachments.length) {
    return <Alert severity="info">No hay documentos sugeridos disponibles en el checklist.</Alert>;
  }

  return (
    <Stack spacing={1}>
      {attachments.map((attachment) => (
        <Paper key={attachment.mediaId} variant="outlined" sx={{ px: 1.5, py: 0.5 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Checkbox
              checked={selectedIds.has(attachment.mediaId)}
              onChange={() => onToggle(attachment.mediaId)}
              inputProps={{ 'aria-label': `Seleccionar ${attachment.fileName}` }}
            />
            <AttachFile fontSize="small" color="action" />
            <Box flex={1} minWidth={0}>
              <Link href={attachment.url} target="_blank" rel="noopener noreferrer">
                {attachment.fileName}
              </Link>
              <Typography variant="caption" color="text.secondary" display="block">
                {attachment.taskLabel}
              </Typography>
            </Box>
          </Box>
        </Paper>
      ))}
    </Stack>
  );
};

const NotaryEmailDraftDialog = ({ open, investmentId, onClose }: NotaryEmailDraftDialogProps) => {
  const [tab, setTab] = useState(0);
  const [values, setValues] = useState<NotaryEmailDraftFormValues>(
    createNotaryEmailDraftFormValues
  );
  const [draft, setDraft] = useState<NotaryEmailDraftDTO>();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<Set<string>>(new Set());
  const [generationError, setGenerationError] = useState<string>();
  const generatedForCurrentOpen = useRef(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { data: currentUser } = useGetMe();
  const { mutateAsync: generateDraft, isPending } = useGenerateNotaryEmailDraft(investmentId);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!currentUser) return;
    setValues((current) => ({
      ...current,
      senderFullName:
        current.senderFullName ||
        [currentUser.name, currentUser.surnames].filter(Boolean).join(' '),
      senderPhone: current.senderPhone || currentUser.phone || '',
    }));
  }, [currentUser]);

  const applyDraft = useCallback((nextDraft: NotaryEmailDraftDTO) => {
    setDraft(nextDraft);
    setSubject(nextDraft.subject);
    setBody(nextDraft.text);
    setSelectedAttachmentIds(
      new Set(nextDraft.attachments.map((attachment) => attachment.mediaId))
    );
    setGenerationError(undefined);
  }, []);

  useEffect(() => {
    if (!open) {
      generatedForCurrentOpen.current = false;
      return;
    }
    if (generatedForCurrentOpen.current) return;
    generatedForCurrentOpen.current = true;

    void generateDraft(toNotaryEmailDraftRequest(values))
      .then(applyDraft)
      .catch((error: unknown) => setGenerationError(getApiErrorMessage(error)));
  }, [applyDraft, generateDraft, open, values]);

  useEffect(() => {
    if (open) contentRef.current?.scrollTo({ top: 0 });
  }, [open, tab]);

  const handleGenerate = async () => {
    try {
      const nextDraft = await generateDraft(toNotaryEmailDraftRequest(values));
      applyDraft(nextDraft);
      setTab(1);
      enqueueSnackbar('Borrador actualizado', { variant: 'success' });
    } catch (error) {
      const message = getApiErrorMessage(error);
      setGenerationError(message);
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleCopy = async (content: string, confirmation: string) => {
    try {
      await navigator.clipboard.writeText(content);
      enqueueSnackbar(confirmation, { variant: 'success' });
    } catch {
      enqueueSnackbar('No se pudo copiar al portapapeles', { variant: 'error' });
    }
  };

  const handleCopyBody = async () => {
    if (draft && body === draft.text && navigator.clipboard.write) {
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/plain': new Blob([body], { type: 'text/plain' }),
            'text/html': new Blob([draft.html], { type: 'text/html' }),
          }),
        ]);
        enqueueSnackbar('Cuerpo del email copiado con formato', { variant: 'success' });
        return;
      } catch {
        // Fall back to plain text for browsers that reject rich clipboard writes.
      }
    }
    await handleCopy(body, 'Cuerpo del email copiado');
  };

  const updateGroup = (group: 'buyers' | 'sellers', nextValue: NotaryPartyGroupFormValue) => {
    setValues((current) => ({ ...current, [group]: nextValue }));
  };

  const proofAttachments = draft?.attachments.filter(
    (attachment) => attachment.taskSlug === 'contrato-arras'
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 'calc(100% - 32px)' },
          height: { xs: '100dvh', sm: 'min(900px, calc(100dvh - 48px))' },
          maxHeight: { xs: '100dvh', sm: 'calc(100dvh - 48px)' },
          m: { xs: 0, sm: 2 },
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden',
          overscrollBehavior: 'contain',
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: 2, flexShrink: 0 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <MailOutline color="primary" />
          <Typography component="span" variant="h6" sx={{ textWrap: 'balance' }}>
            Borrador de email para notaría
          </Typography>
        </Box>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, nextTab: number) => setTab(nextTab)}
        aria-label="Editor del borrador para notaría"
        sx={{
          px: { xs: 1, sm: 3 },
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
          '& .MuiTab-root': {
            minWidth: { xs: 0, sm: 160 },
            flex: { xs: 1, sm: 'initial' },
          },
        }}
      >
        <Tab label="Datos del caso" />
        <Tab label="Borrador" disabled={!draft} />
      </Tabs>

      <DialogContent
        ref={contentRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          scrollbarGutter: 'stable',
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
        }}
      >
        {generationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {generationError}
          </Alert>
        )}

        {tab === 0 && (
          <Stack spacing={2}>
            <Alert severity="info">
              Los campos vacíos se precargan desde la inversión y los perfiles cuando sea posible.
              Nada se guarda ni se envía al generar el borrador.
            </Alert>

            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Remitente</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nombre del remitente"
                      value={values.senderFullName}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          senderFullName: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Teléfono del remitente"
                      value={values.senderPhone}
                      onChange={(event) =>
                        setValues((current) => ({ ...current, senderPhone: event.target.value }))
                      }
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Partes</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={4} divider={<Divider flexItem />}>
                  <PartyGroupFields
                    title="Parte vendedora"
                    value={values.sellers}
                    emptyMessage="No hay vendedores precargados. Añádelos para incluirlos en el borrador."
                    onChange={(value) => updateGroup('sellers', value)}
                  />
                  <PartyGroupFields
                    title="Parte compradora"
                    value={values.buyers}
                    emptyMessage="Se usará el inversor principal. Añade compradores solo si quieres sustituir sus datos."
                    onChange={(value) => updateGroup('buyers', value)}
                  />
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Inmueble y registro</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      label="Descripción"
                      placeholder="Vivienda, local…"
                      value={values.propertyDescription}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          propertyDescription: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={7}>
                    <TextField
                      fullWidth
                      label="Dirección"
                      helperText="Déjala vacía para usar la dirección de la inversión"
                      value={values.propertyAddress}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          propertyAddress: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Referencia catastral"
                      value={values.cadastralReference}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          cadastralReference: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Registro de la Propiedad"
                      value={values.registryOffice}
                      onChange={(event) =>
                        setValues((current) => ({ ...current, registryOffice: event.target.value }))
                      }
                    />
                  </Grid>
                  {(
                    [
                      ['registryPropertyNumber', 'Finca'],
                      ['registryVolume', 'Tomo'],
                      ['registryBook', 'Libro'],
                      ['registryFolio', 'Folio'],
                      ['registryEntry', 'Inscripción'],
                    ] as const
                  ).map(([field, label]) => (
                    <Grid item xs={12} sm={4} key={field}>
                      <TextField
                        fullWidth
                        label={label}
                        value={values[field]}
                        onChange={(event) =>
                          setValues((current) => ({ ...current, [field]: event.target.value }))
                        }
                      />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Operación y arras</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Fecha del acta"
                      placeholder="Ej.: Dime cuándo puede ser"
                      value={values.transparencyActDate}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          transparencyActDate: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Fecha de compraventa"
                      placeholder="Ej.: 31/jul a falta de la hora"
                      helperText="Vacío: usa la fecha de compraventa del checklist"
                      value={values.saleDeedDate}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          saleDeedDate: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, step: '0.01' }}
                      label="Precio total (€)"
                      helperText="Vacío: usa el precio de la inversión"
                      value={values.totalPriceEuros}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          totalPriceEuros: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, step: '0.01' }}
                      label="Importe de arras (€)"
                      value={values.earnestMoneyAmountEuros}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          earnestMoneyAmountEuros: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="number"
                      inputProps={{ min: 0, step: '0.01' }}
                      label="Transferencia (€)"
                      helperText="Vacío: precio menos arras"
                      value={values.transferAmountEuros}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          transferAmountEuros: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Gastos de notaría"
                      value={values.notaryCosts}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          notaryCosts: event.target
                            .value as NotaryEmailDraftFormValues['notaryCosts'],
                        }))
                      }
                    >
                      <MenuItem value="">Sin indicar</MenuItem>
                      <MenuItem value="BUYER">Comprador</MenuItem>
                      <MenuItem value="SELLER">Vendedor</MenuItem>
                      <MenuItem value="SHARED">Según ley</MenuItem>
                      <MenuItem value="OTHER">Otro reparto</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Detalle gastos de notaría"
                      value={values.notaryCostsDetails}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          notaryCostsDetails: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="IBI y basuras"
                      value={values.propertyTaxes}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          propertyTaxes: event.target
                            .value as NotaryEmailDraftFormValues['propertyTaxes'],
                        }))
                      }
                    >
                      <MenuItem value="">Sin indicar</MenuItem>
                      <MenuItem value="PRORATED">Prorrateo</MenuItem>
                      <MenuItem value="BUYER">Comprador</MenuItem>
                      <MenuItem value="SELLER">Vendedor</MenuItem>
                      <MenuItem value="OTHER">Otro reparto</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Detalle IBI y basuras"
                      value={values.propertyTaxesDetails}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          propertyTaxesDetails: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Justificantes de arras
                    </Typography>
                    {proofAttachments?.length ? (
                      <Stack>
                        {proofAttachments.map((attachment) => (
                          <FormControlLabel
                            key={attachment.mediaId}
                            control={
                              <Checkbox
                                checked={values.earnestMoneyProofMediaIds.includes(
                                  attachment.mediaId
                                )}
                                onChange={(_, checked) =>
                                  setValues((current) => ({
                                    ...current,
                                    earnestMoneyProofMediaIds: checked
                                      ? [...current.earnestMoneyProofMediaIds, attachment.mediaId]
                                      : current.earnestMoneyProofMediaIds.filter(
                                          (mediaId) => mediaId !== attachment.mediaId
                                        ),
                                  }))
                                }
                              />
                            }
                            label={attachment.fileName}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No hay documentos en la tarea de contrato de arras.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Poderes e hipoteca</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Poderes"
                      value={values.powersStatus}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          powersStatus: event.target
                            .value as NotaryEmailDraftFormValues['powersStatus'],
                        }))
                      }
                    >
                      <MenuItem value="">Sin indicar</MenuItem>
                      <MenuItem value="NONE">No hay</MenuItem>
                      <MenuItem value="EXISTS">Hay poderes</MenuItem>
                      <MenuItem value="UNKNOWN">Desconocido</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Detalle de los poderes"
                      value={values.powersDetails}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          powersDetails: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Banco"
                      placeholder="Ej.: Sabadell"
                      value={values.mortgageBank}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          mortgageBank: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Estado de la FEIN"
                      placeholder="Ej.: No está firmada aún, llegará en breve"
                      value={values.mortgageFein}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          mortgageFein: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography fontWeight={600}>Comunidad, CEE y observaciones</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Comunidad de propietarios"
                      value={values.communityStatus}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          communityStatus: event.target
                            .value as NotaryEmailDraftFormValues['communityStatus'],
                        }))
                      }
                    >
                      <MenuItem value="">Sin indicar</MenuItem>
                      <MenuItem value="CONSTITUTED">Constituida</MenuItem>
                      <MenuItem value="NOT_CONSTITUTED">No constituida</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Detalles de la comunidad"
                      value={values.communityDetails}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          communityDetails: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      fullWidth
                      label="Estado del CEE"
                      value={values.energyCertificateStatus}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          energyCertificateStatus: event.target
                            .value as NotaryEmailDraftFormValues['energyCertificateStatus'],
                        }))
                      }
                    >
                      <MenuItem value="">Sin indicar</MenuItem>
                      <MenuItem value="AVAILABLE">Disponible</MenuItem>
                      <MenuItem value="PENDING">Pendiente</MenuItem>
                      <MenuItem value="NOT_APPLICABLE">No aplica</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Disponible el"
                      InputLabelProps={{ shrink: true }}
                      value={values.energyCertificateAvailableOn}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          energyCertificateAvailableOn: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Detalles del CEE"
                      value={values.energyCertificateDetails}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          energyCertificateDetails: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Documentos adicionales"
                      helperText="Una línea por documento, por ejemplo: DNIs vendedores (el día de la firma)"
                      value={values.documentNotes}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          documentNotes: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Observaciones adicionales"
                      helperText="Una observación por línea"
                      value={values.additionalNotes}
                      onChange={(event) =>
                        setValues((current) => ({
                          ...current,
                          additionalNotes: event.target.value,
                        }))
                      }
                    />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}

        {tab === 1 && isPending && !draft && (
          <Box minHeight={400} display="flex" alignItems="center" justifyContent="center">
            <Stack alignItems="center" spacing={2}>
              <CircularProgress />
              <Typography color="text.secondary">Generando borrador…</Typography>
            </Stack>
          </Box>
        )}

        {tab === 1 && draft && (
          <Stack spacing={3}>
            <Alert severity={draft.readiness.ready ? 'success' : 'warning'}>
              {draft.readiness.ready
                ? 'Las tareas obligatorias de presentación están completadas.'
                : `Quedan ${draft.readiness.pendingRequiredTasks.length} tareas obligatorias de presentación sin completar.`}
            </Alert>

            {draft.warnings.length > 0 && (
              <Alert severity="warning" icon={false}>
                <Typography fontWeight={700} gutterBottom>
                  Revisa {draft.warnings.length === 1 ? 'este aviso' : 'estos avisos'} antes de
                  copiar el email
                </Typography>
                <List dense disablePadding>
                  {draft.warnings.map((warning, index) => (
                    <ListItem key={`${warning.code}-${warning.path ?? warning.taskSlug}-${index}`}>
                      <ListItemText primary={warning.message} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            )}

            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Asunto
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopy />}
                  onClick={() => handleCopy(subject, 'Asunto copiado')}
                >
                  Copiar
                </Button>
              </Box>
              <TextField
                fullWidth
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
              />
            </Box>

            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Cuerpo del email
                </Typography>
                <Button size="small" startIcon={<ContentCopy />} onClick={handleCopyBody}>
                  Copiar
                </Button>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={18}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                inputProps={{ style: { fontFamily: 'monospace', lineHeight: 1.5 } }}
              />
            </Box>

            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Adjuntos sugeridos
                </Typography>
                <Chip
                  size="small"
                  label={`${selectedAttachmentIds.size}/${draft.attachments.length} seleccionados`}
                />
              </Box>
              <AttachmentList
                attachments={draft.attachments}
                selectedIds={selectedAttachmentIds}
                onToggle={(mediaId) =>
                  setSelectedAttachmentIds((current) => {
                    const next = new Set(current);
                    if (next.has(mediaId)) next.delete(mediaId);
                    else next.add(mediaId);
                    return next;
                  })
                }
              />
            </Box>

            <Alert severity="info">
              La selección de adjuntos es solo una ayuda para preparar el envío. Este diálogo no
              guarda, descarga ni envía documentos.
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flexShrink: 0,
          justifyContent: 'flex-end',
          borderTop: 1,
          borderColor: 'divider',
          px: { xs: 2, sm: 3 },
          pt: 1.5,
          pb: { xs: 'max(12px, env(safe-area-inset-bottom))', sm: 1.5 },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          sx={{
            width: { xs: '100%', sm: 'auto' },
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            '& .MuiButton-root': { flex: { xs: '1 1 auto', sm: 'initial' } },
          }}
        >
          <Button onClick={onClose}>Cerrar</Button>
          {tab === 0 ? (
            <Button
              variant="contained"
              startIcon={isPending ? <CircularProgress size={18} /> : <Refresh />}
              disabled={isPending}
              onClick={handleGenerate}
            >
              Generar borrador
            </Button>
          ) : (
            <>
              <Button variant="outlined" onClick={() => setTab(0)}>
                Editar datos
              </Button>
              <Button
                variant="contained"
                startIcon={<ContentCopy />}
                disabled={!draft}
                onClick={() =>
                  handleCopy(`Asunto: ${subject}\n\n${body}`, 'Email copiado al portapapeles')
                }
              >
                Copiar email
              </Button>
            </>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default NotaryEmailDraftDialog;
